import { usePatientConfiguration } from '@/features/patient-configuration/api';
import { z } from 'zod';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Mic, Pencil, Plus, Save, Send, Sparkles, Upload, User } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useMembers } from '@/features/members';
import { useOrgDateFormat } from '@/lib/timezone';
import { audioDB, STALE_AUDIO_TTL_MS } from '@/lib/storage/audioDB';
import { mergeStoredRecordings } from '@/lib/storage/recordings-merge';
import { ageFromIso, isoToDate } from '@/lib/utils/date';
import { ENCOUNTER_TYPES, encounterTypeToggleColors } from '../constants';
import { useCreateEncounter } from '../api/encounters-api';
import { AudioBar } from '@/components/encounter/AudioBar';
import { P, PermissionGuard } from '@/features/permissions';
import { RecordingPanel } from '@/components/encounter/RecordingPanel';
import { ReferencePanel } from '@/components/encounter/ReferencePanel';
import type { RecordingsListRef } from '@/components/audio/RecordingsList';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { type PatientOut, useCreatePatient, usePatients } from '../api/patients-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePregnancies } from '@/features/emr/api/pregnancies-api';
import { usePickerTemplates } from '@/features/templates/api/templates-api';
import { type EncounterFormDraft, useEncounterSessionStore } from '@/lib/stores/encounter-session-store';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ─── Schema ──────────────────────────────────────────────────────

const encounterFormSchema = z.object({
  assigned_to: z.string().optional(),
  patient_id: z.string().min(1, 'Patient is required'),
  template_id: z.string().min(1, 'Template is required'),
  encounter_date: z.string().min(1, 'Encounter date is required'),
  encounter_type: z.enum(ENCOUNTER_TYPES, {
    message: 'Encounter type is required',
  }),
});

type EncounterFormValues = z.infer<typeof encounterFormSchema>;

// ─── Helpers ─────────────────────────────────────────────────────

export function NewEncounterPage() {
  return (
    <PermissionGuard permission={P.CREATE_ENCOUNTER}>
      <NewEncounterPageContent />
    </PermissionGuard>
  );
}

function NewEncounterPageContent() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const formRef = useRef<HTMLFormElement>(null);
  const submitTypeRef = useRef<'draft' | 'submit'>('draft');
  // Only the Save Draft / Submit buttons may create an encounter. A stray
  // submit (Enter in a field, an untyped button in a child panel) used to
  // save a draft silently and leave the clinician on a fresh, today-dated form.
  const submitIntentRef = useRef(false);
  // Set synchronously so a double tap cannot post twice while validation runs.
  const inFlightRef = useRef(false);
  const recordingsListRef = useRef<RecordingsListRef>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [patientOpen, setPatientOpen] = useState(false);
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const { formDraft, setFormDraft, clearSession, recordingMeta, setOriginRoute } = useEncounterSessionStore();
  const { today } = useOrgDateFormat();

  const { data: contextViews } = usePickerTemplates({ category: 'context_view' });
  const contextViewTemplates = contextViews ?? [];
  const [contextViewId, setContextViewId] = useState<string | null>(null);
  // Serialize recovery scans so a tight async race between an IDB read
  // and a React re-render can't run two concurrent merges and double-add
  // the same blob. The helper is pure; the mutex is on the caller.
  const recoveryInFlightRef = useRef(false);

  // Auto-select "General Visit" as default context view
  useEffect(() => {
    if (!contextViewId && contextViewTemplates.length > 0) {
      const generalView = contextViewTemplates.find((t) => t.name === 'General Visit');
      if (generalView) setContextViewId(generalView.id);
    }
  }, [contextViewId, contextViewTemplates]);

  // On fresh mount with no active session, sweep STALE blobs from a prior
  // abandoned session — see QuickStartPage for the full rationale. The
  // TTL preserves blobs from the current tab's just-stopped recording
  // and from a parallel tab's active session, while clearing anything
  // old enough to be a leftover.
  const cleanupRanRef = useRef(false);
  useEffect(() => {
    if (cleanupRanRef.current) return;
    if (recordingMeta.status !== 'idle') return;
    cleanupRanRef.current = true;
    audioDB.deleteStale(STALE_AUDIO_TTL_MS).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to prune stale audio recordings on mount', err);
    });
  }, [recordingMeta.status]);

  // Single source of truth for the recordings list: audioDB. The recorder
  // writes the blob there in `stopRecording()` BEFORE flipping
  // `recordingMeta.status` to `'stopped'`, so this effect picks it up
  // exactly once. ``mergeStoredRecordings`` is the pure helper covered by
  // ``recordings-merge.test.ts``.
  //
  // Recovery is now gated to status === 'stopped' only — the prior 'idle'
  // path was the leak vector that pulled stale blobs from prior sessions.
  useEffect(() => {
    if (recordingMeta.status !== 'stopped') {
      return;
    }
    if (recoveryInFlightRef.current) return;
    recoveryInFlightRef.current = true;
    async function recoverRecordings() {
      try {
        const stored = await audioDB.getAll();
        const existing = recordingsListRef.current?.getRecordings() ?? [];
        for (const rec of mergeStoredRecordings(existing, stored)) {
          recordingsListRef.current?.addRecording(rec);
        }
      } finally {
        recoveryInFlightRef.current = false;
      }
    }
    void recoverRecordings();
  }, [recordingMeta.status]);

  const createEncounter = useCreateEncounter();

  const form = useForm<EncounterFormValues>({
    resolver: zodResolver(encounterFormSchema),
    defaultValues: {
      patient_id: formDraft.patient_id ?? '',
      assigned_to: formDraft.assigned_to ?? '',
      template_id: formDraft.template_id ?? '',
      encounter_date: formDraft.encounter_date ?? today(),
      encounter_type: formDraft.encounter_type ?? undefined,
    },
  });

  // ─── Store sync effects ─────────────────────────────────────

  useEffect(() => {
    setOriginRoute('/encounters/new');
  }, [setOriginRoute]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const subscription = form.watch((values) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setFormDraft(values as Partial<EncounterFormDraft>);
      }, 500);
    });
    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [form, setFormDraft]);

  const submittedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!submittedRef.current) {
        setFormDraft(form.getValues());
      }
    };
  }, [form, setFormDraft]);

  const handleTemplateChange = useCallback(
    (templateId: string) => {
      form.setValue('template_id', templateId);
    },
    [form],
  );

  // ─── Header actions ──────────────────────────────────────────

  const handleSaveDraft = () => {
    submitTypeRef.current = 'draft';
    submitIntentRef.current = true;
    formRef.current?.requestSubmit();
  };

  const handleSubmit = () => {
    submitTypeRef.current = 'submit';
    submitIntentRef.current = true;
    formRef.current?.requestSubmit();
  };

  useEffect(() => {
    setPageHeader({
      title: 'Create New Encounter',
      actions: (
        <div className="flex gap-2">
          <Button variant="outline" disabled={isLoading} aria-label="Save as Draft" onClick={handleSaveDraft}>
            {isLoading && submitTypeRef.current === 'draft' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="hidden md:inline">
              {isLoading && submitTypeRef.current === 'draft' ? 'Saving...' : 'Save Draft'}
            </span>
          </Button>
          <Button size="lg" className="min-h-12 min-w-32" disabled={isLoading} aria-label="Submit Encounter" onClick={handleSubmit}>
            {isLoading && submitTypeRef.current === 'submit' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>
              {isLoading && submitTypeRef.current === 'submit' ? 'Submitting...' : 'Submit'}
            </span>
          </Button>
        </div>
      ),
    });

    return () => setPageHeader(null);
  }, [setPageHeader, isLoading]);

  // ─── Form submission ─────────────────────────────────────────

  const onSubmit = async (values: EncounterFormValues) => {
    if (inFlightRef.current) return;
    const isSubmit = submitTypeRef.current === 'submit';
    const recordings = recordingsListRef.current?.getRecordings() ?? [];

    // Submitting requires at least one audio file
    if (isSubmit && recordings.length === 0) {
      toast.error('At least one audio recording is required to submit an encounter');
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const clinicalContext: Record<string, unknown> = {
        template_id: values.template_id || undefined,
        context_view_template_id: contextViewId || undefined,
      };

      const encounterData: Record<string, unknown> = {
        patient_id: values.patient_id,
        encounter_date: values.encounter_date,
        encounter_type: values.encounter_type,
        ...(values.assigned_to ? { assigned_to: values.assigned_to } : {}),
        clinical_context: clinicalContext,
      };

      const audioFiles = recordings
        .filter((r) => r.blob != null)
        .map(
          (r, idx) => new File([r.blob!], r.name || `recording-${idx}.webm`, { type: r.blob!.type || 'audio/webm' }),
        );

      const result = await createEncounter.mutateAsync({
        files: audioFiles,
        encounter_data: encounterData,
        status: isSubmit ? 'submitted' : 'draft',
      });

      toast.success(isSubmit ? 'Encounter submitted' : 'Encounter saved as draft');

      submittedRef.current = true;
      // Recordings uploaded to the server; drop the local IndexedDB cache
      // so a fresh `/encounters/new` visit doesn't surface stale audio.
      await audioDB.deleteAll();
      clearSession();
      navigate({
        to: '/encounters/$encounterId',
        params: { encounterId: result.id },
      });
    } catch {
      toast.error('Failed to create encounter');
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  // ─── Active patients & members ──────────────────────────────

  const { patientId: preselectedPatientId } = useSearch({ from: '/_authenticated/encounters/new' });

  const { data: patientsData } = usePatients({ limit: 100 });
  const { data: membersData } = useMembers();
  const activePatients = (patientsData?.data ?? []).filter((p) => !p.deleted_at);
  const activeMembers = (membersData ?? []).filter((m) => m.is_active);

  // ─── Pre-select patient from search params ──────────────────

  const patientPreselected = useRef(false);
  useEffect(() => {
    if (preselectedPatientId && activePatients.length > 0 && !patientPreselected.current) {
      const patient = activePatients.find((p) => p.id === preselectedPatientId);
      if (patient) {
        form.setValue('patient_id', patient.id);
        patientPreselected.current = true;
      }
    }
  }, [preselectedPatientId, activePatients, form]);

  // ─── Selected patient info ──────────────────────────────────

  const selectedPatientId = form.watch('patient_id');
  const selectedPatient = activePatients.find((p) => p.id === selectedPatientId);

  // Active pregnancy for the selected patient — drives OB flow-sheet routing.
  const { data: pregnanciesData } = usePregnancies(selectedPatientId || undefined);
  const activePregnancyId = pregnanciesData?.data?.find((p) => p.status === 'active')?.id ?? null;
  const selectedPatientMeta = selectedPatient?.metadata_;
  const selectedPatientName = selectedPatient
    ? `${selectedPatientMeta?.last_name ?? ''}, ${selectedPatientMeta?.first_name ?? ''}`
    : '';
  // isoToDate, not new Date(): a date-only string parsed as UTC midnight renders
  // a day early under a negative offset.
  const selectedPatientDobDate = isoToDate(selectedPatientMeta?.date_of_birth ?? '');
  const selectedPatientDob = selectedPatientDobDate ? format(selectedPatientDobDate, 'MMM d, yyyy') : null;
  const selectedPatientGender =
    selectedPatientMeta?.gender === 'M'
      ? 'Male'
      : selectedPatientMeta?.gender === 'F'
        ? 'Female'
        : (selectedPatientMeta?.gender ?? null);

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div className="relative mt-4 px-4 overflow-hidden">
      <Form {...form}>
        <form
          ref={formRef}
          className="space-y-6"
          onSubmit={(event) => {
            if (!submitIntentRef.current) {
              event.preventDefault();
              return;
            }
            submitIntentRef.current = false;
            void form.handleSubmit(onSubmit)(event);
          }}>
          {/* ─── Patient Selector (above grid) ─────────────────── */}
          <FormField
            name="patient_id"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                    <User className="h-4.5 w-4.5 text-muted-foreground" />
                  </div>

                  {selectedPatient ? (
                    <div className="flex-1 min-w-0">
                      <p className="text-xl font-semibold tracking-tight md:text-2xl">{selectedPatientName}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>MRN: {selectedPatient.medical_record_number}</span>
                        {selectedPatientDob && (
                          <>
                            <span className="text-border">&middot;</span>
                            <span>DOB: {selectedPatientDob}</span>
                          </>
                        )}
                        {selectedPatientGender && (
                          <>
                            <span className="text-border">&middot;</span>
                            <span>{selectedPatientGender}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">No patient selected</p>
                    </div>
                  )}

                  <Popover open={patientOpen} onOpenChange={setPatientOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button size="sm" role="combobox" variant="outline" className="shrink-0">
                          <ChevronsUpDown className="h-3.5 w-3.5 mr-1.5" />
                          {selectedPatient ? 'Change' : 'Select Patient'}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[320px] p-0">
                      <Command>
                        <CommandInput placeholder="Search patients..." />
                        <CommandList>
                          <CommandEmpty>No patients found.</CommandEmpty>
                          <CommandGroup>
                            {activePatients.map((patient) => (
                              <CommandItem
                                key={patient.id}
                                value={getPatientLabel(patient)}
                                onSelect={() => {
                                  field.onChange(patient.id);
                                  setPatientOpen(false);
                                }}>
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    field.value === patient.id ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {patient.metadata_?.last_name ?? ''}, {patient.metadata_?.first_name ?? ''}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    MRN: {patient.medical_record_number}
                                    {patient.metadata_?.date_of_birth && (
                                      <>
                                        {' — '}
                                        {ageFromIso(patient.metadata_?.date_of_birth)}y
                                      </>
                                    )}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                        <div className="border-t p-1">
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                            onClick={() => {
                              setPatientOpen(false);
                              setNewPatientOpen(true);
                            }}>
                            <Plus className="h-4 w-4" />
                            Create New Patient
                          </button>
                        </div>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ─── AudioBar (replaces inline audio section) ──────────
              Don't add to the in-memory list on stop. `stopRecording()`
              writes the blob to audioDB BEFORE flipping
              `recordingMeta.status` to `'stopped'`, so the recovery
              effect above picks it up exactly once. The previous
              dual-path produced duplicate rows on iPad. */}
          <AudioBar />

          {/* ─── Two-Column Grid ───────────────────────────────── */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left: Clinical Context */}
            <div className="min-w-0 space-y-6">
              {/* Context View selector */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Context View:</Label>
                <Select value={contextViewId ?? ''} onValueChange={(v) => setContextViewId(v || null)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select view..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contextViewTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ─── Encounter Details ──────────────────────────── */}
              <Card className="border-border/60">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Pencil className="h-3.5 w-3.5" />
                    </div>
                    Encounter Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Encounter Type */}
                  <FormField
                    name="encounter_type"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm">
                          Encounter Type <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <ToggleGroup
                            type="single"
                            variant="outline"
                            className="w-full flex-wrap"
                            value={field.value ?? ''}
                            onValueChange={(value) => {
                              if (value) field.onChange(value);
                            }}>
                            {ENCOUNTER_TYPES.map((type) => (
                              <ToggleGroupItem
                                key={type}
                                value={type}
                                className={cn('capitalize text-sm', encounterTypeToggleColors[type])}>
                                {type === 'telephone' ? 'Phone call' : type}
                              </ToggleGroupItem>
                            ))}
                          </ToggleGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Date + Clinician in 2-col grid */}
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      name="encounter_date"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">
                            Encounter Date <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="assigned_to"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Assigned Clinician</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select clinician (optional)..." />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {activeMembers.map((member) => (
                                <SelectItem key={member.user_id} value={member.user_id}>
                                  {member.user_name ?? member.user_email ?? 'Unknown'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* ─── Template + Audio (Plan 07: shared with Quick Start) ─── */}
              <RecordingPanel
                recordingsListRef={recordingsListRef}
                templateId={form.watch('template_id') ?? ''}
                onTemplateChange={handleTemplateChange}
              />
              {form.formState.errors.template_id && (
                <p className="text-sm font-medium text-destructive">
                  {form.formState.errors.template_id.message as string}
                </p>
              )}
            </div>

            {/* Right: Reference Panel (visible only when patient selected) */}
            {selectedPatientId && (
              <div className="min-w-0 min-h-[400px] lg:sticky lg:top-4 lg:self-start">
                <ReferencePanel
                  patientId={selectedPatientId}
                  pregnancyId={activePregnancyId}
                  contextViewTemplateId={contextViewId}
                />
              </div>
            )}
          </div>
        </form>
      </Form>

      <CreatePatientDialog
        open={newPatientOpen}
        onOpenChange={setNewPatientOpen}
        onCreated={(patient) => {
          form.setValue('patient_id', patient.id);
        }}
      />
    </div>
  );
}

// ─── Create Patient Dialog ─────────────────────────────────────

const newPatientSchema = z.object({
  last_name: z.string().min(1, 'Last name is required'),
  first_name: z.string().min(1, 'First name is required'),
  medical_record_number: z.string().min(1, 'MRN is required'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  gender: z.enum(['M', 'F', 'O'], { message: 'Gender is required' }),
});

type NewPatientValues = z.infer<typeof newPatientSchema>;

function CreatePatientDialog({
  open,
  onCreated,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (patient: PatientOut) => void;
}) {
  const createPatient = useCreatePatient();
  const config = usePatientConfiguration();
  const automaticMrn = config.data && config.data.mrn.mode !== 'manual';
  const form = useForm<NewPatientValues>({
    resolver: zodResolver(
      automaticMrn ? newPatientSchema.extend({ medical_record_number: z.string() }) : newPatientSchema,
    ),
    defaultValues: {
      last_name: '',
      first_name: '',
      date_of_birth: '',
      gender: undefined,
      medical_record_number: '',
    },
  });

  const onSubmit = async (values: NewPatientValues) => {
    try {
      const patient = await createPatient.mutateAsync({
        medical_record_number: values.medical_record_number || undefined,
        metadata_: {
          gender: values.gender,
          last_name: values.last_name,
          first_name: values.first_name,
          date_of_birth: values.date_of_birth,
        },
      });
      toast.success('Patient created');
      form.reset();
      onOpenChange(false);
      onCreated(patient);
    } catch {
      toast.error('Failed to create patient');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Patient</DialogTitle>
          <DialogDescription>Add a new patient record. You can add more details later.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="first_name"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="last_name"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Smith" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="date_of_birth"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Date of Birth <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="medical_record_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      MRN{' '}
                      {automaticMrn ? (
                        '(leave blank to assign automatically)'
                      ) : (
                        <span className="text-destructive">*</span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. MRN-001234" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              name="gender"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Gender <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      className="w-full"
                      value={field.value ?? ''}
                      onValueChange={(value) => {
                        if (value) field.onChange(value);
                      }}>
                      <ToggleGroupItem
                        value="M"
                        className="flex-1 data-[state=on]:bg-info-surface data-[state=on]:text-info">
                        Male
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="F"
                        className="flex-1 data-[state=on]:bg-category-rose-surface data-[state=on]:text-category-rose">
                        Female
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="O"
                        className="flex-1 data-[state=on]:bg-category-plum-surface data-[state=on]:text-category-plum">
                        Other
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createPatient.isPending}>
                {createPatient.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Patient
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function getPatientLabel(patient: PatientOut | undefined): string {
  if (!patient) return '';
  const meta = patient.metadata_;
  const name = `${meta?.last_name ?? ''}, ${meta?.first_name ?? ''}`;
  const years = ageFromIso(meta?.date_of_birth);
  const age = years === undefined ? '' : `${years}y`;
  return `${name} — MRN: ${patient.medical_record_number}${age ? ` — ${age}` : ''}`;
}
